import styled from 'styled-components';
import isArray from 'lodash/isArray';
import FieldRadio from 'hive-admin/src/components/FieldRadio';

import getFilterWithCol from '../../helpers/getFilterWithCol';
import clickAnchor from '../../helpers/clickAnchor';

import Types from '../../modules/types';

import tests from '../../helpers/tests';

const RadioComponent = styled(FieldRadio.config.Component)`
  display: flex;
  width: 100%;
  > .ant-radio-button-wrapper {
    flex: 1;
    flex-shrink: 1;
    align-items: center;
    justify-content: center;
    text-align: center;
    height: auto;
    line-height: 100%;
    display: flex;
    padding: 8px 5px;
    white-space: nowrap;
  }
`;

const getValueForFieldDefault = value => (
  value && value.length
  ? value
  : undefined
);

export const pagination = ['FilterPagination', {
  id: 'pagination',
  section: 'bottom',
  defaultPageSize: 24,
  pageSizes: ['24', '48', '96'],
}];

export const populateCategory = ['FilterHidden', {
  id: 'populateCategory',
  build: builder => builder.add('populate', { category: true }),
}];

export const populateStore = ['FilterHidden', {
  id: 'populateStore',
  build: builder => builder.add(
    'populate',
    { store: { name: true, reviewSupported: true, trn: 1 } },
  ),
}];

export const scopeToSeller = ['FilterHidden', {
  id: 'scopeToSeller',
  build: builder => builder.add('custom', { scopeToSellerStore: true }),
}];

export const filterCatalogue = ['FilterHidden', {
  id: 'filterCatalogue',
  build: (builder) => builder.add('custom', { catalogue: true }),
}];

export const filterName = ['FilterField', {
  id: 'name',
  label: null,
  section: 'top',
  buildQuery: (value, builder) => value && builder.add(
    'where',
    {
      OR: [
        { name: { REGEX: value, OPTIONS: 'i' } },
        { 'variations.sku': { REGEX: value, OPTIONS: 'i' } },
        { 'variations.storeSku': { REGEX: value, OPTIONS: 'i' } },
        { 'variations.barcode': { REGEX: value, OPTIONS: 'i' } },
      ],
    },
  ),
  getValueForField: value => value || '',
  getValueForQuery: (value) => {
    value = !value
    ? undefined
    : value.target
    ? value.target.value
    : value;
    return !value || !value.length
    ? undefined
    : value;
  },
  field: ['FieldText', {
    name: 'name',
    placeholder: 'Find By Name, SKU, Store SKU or Barcode',
  }],
}];

export const filterStore = ['FilterField', {
  id: 'store',
  label: null,
  section: 'top',
  propsFromPage: props => ({ client: props.client }),
  buildQuery: (value, builder) => value && value.length && builder.add(
    'where',
    { store: { IN: isArray(value) ? value : [value] } },
  ),
  getValueForField: getValueForFieldDefault,
  field: ['FieldConnectionSelect', {
    name: 'store',
    url: '/stores/list',
    placeholder: 'Find By Store',
    mode: 'multiple',
    allowClear: true,
    searchPaths: ['name'],
    // getChoiceLabel: choice => `${(choice.data.name)}`,
  }],
}];

export const filterProblems = ['FilterField', {
  id: 'problems',
  label: null,
  section: 'top',
  buildQuery: (value, builder) => value && value.length && builder.add(
    'custom',
    { problems: isArray(value) ? value : [value] },
  ),
  getValueForField: getValueForFieldDefault,
  field: ['FieldSelect', {
    name: 'problems',
    prefix: 'Problems:',
    initialValue: [],
    // prepareValueForInput: value => (value ? value : 'any'),
    mode: 'multiple',
    allowClear: true,
    choices: [{
      label: 'Missing Size',
      value: 'missing-size',
    }, {
      label: 'Missing Weight',
      value: 'missing-weight',
    }],
  }],
}];

export const filterCategory = ['FilterField', {
  id: 'category',
  label: null,
  section: 'top',
  propsFromPage: props => ({ client: props.client }),
  buildQuery: (value, builder) => value && value.length && builder.add(
    'where',
    {
      OR: [
        { category: { IN: isArray(value) ? value : [value] } },
        { categories: { IN: isArray(value) ? value : [value] } },
      ],
    },
  ),
  getValueForField: getValueForFieldDefault,
  field: ['FieldConnectionSelect', {
    name: 'category',
    url: '/categories',
    placeholder: 'Find By Category',
    mode: 'multiple',
    allowClear: true,
    searchPaths: ['name'],
    extraQueryProps: { scopeToSellerStore: true },
    // getChoiceLabel: choice => `${(choice.data.name)}`,
  }],
}];

export const filterCollection = ['FilterField', {
  id: 'collection',
  label: null,
  section: 'top',
  propsFromPage: props => ({ client: props.client }),
  buildQuery: (value, builder) => value && value.length && builder.add(
    'where',
    { collections: { IN: isArray(value) ? value : [value] } },
  ),
  getValueForField: getValueForFieldDefault,
  field: ['FieldConnectionSelect', {
    name: 'collection',
    url: '/collections',
    placeholder: 'Find By Collection',
    mode: 'multiple',
    allowClear: true,
    searchPaths: ['name'],
    extraQueryProps: { scopeToSellerStore: true },
    // getChoiceLabel: choice => `${(choice.data.name)}`,
  }],
}];

export const filterType = ['FilterField', {
  id: 'type',
  label: null,
  section: 'top',
  VALUES_MAP: {
    any: null,
    merchant: { type: { EQ: 'MERCHANT_PRODUCT' } },
    referral: { type: { EQ: 'REFERRAL_PRODUCT' } },
    reseller: { type: { EQ: 'RESELLER_PRODUCT' } },
    service: { kind: { EQ: 'SERVICE' } },
  },
  buildQuery: (value, builder, filter) => (
    filter.VALUES_MAP[value]
    ? builder.add('where', { ...filter.VALUES_MAP[value] })
    : null
  ),
  getValueForField: value => (
    value && value.length
    ? value
    : undefined
  ),
  field: ['FieldSelect', {
    name: 'type',
    placeholder: 'Type',
    prepareValueForInput: value => (value ? value : 'any'),
    choices: [{
      label: 'Any Product Type',
      value: 'any',
    }, {
      label: 'Merchant',
      value: 'merchant',
    }, {
      label: 'Reseller',
      value: 'reseller',
    }, {
      label: 'Referral',
      value: 'referral',
    }, {
      label: 'Service',
      value: 'service',
    }],
  }],
}];

export const filterPublished = ['FilterField', {
id: 'published',
  label: null,
  section: 'top',
  VALUES_MAP: {
    all: {},
    published: { publishedAt: { LTE: Date.now() } },
    upcoming: { publishedAt: { GT: Date.now() } },
  },
  buildQuery: (value, builder, filter) => (
    filter.VALUES_MAP[value]
    ? builder.add('where', { ...filter.VALUES_MAP[value] })
    : builder.add('where', { ...filter.VALUES_MAP.all })
  ),
  getValueForField: value => (
      value && value.length
    ? value
    : 'all'
  ),
  field: ['FieldRadio', {
    name: 'published',
    label: null,
    initialValue: 'all',
    choices: [{
      label: 'All',
      value: 'all',
    }, {
      label: 'Published',
      value: 'published',
    }, {
      label: 'Upcoming',
      value: 'upcoming',
    }],
    Component: RadioComponent,
  }],
}];

export const filterStorageType = ['FilterField', {
  id: 'storageType',
  label: null,
  section: 'top',
  VALUES_MAP: {
    any: null,
    local: { 'stockAggregates.LOCAL': { GTE: 1 } },
    warehouse: { 'stockAggregates.WAREHOUSE': { GTE: 1 } },
  },
  buildQuery: (value, builder, filter) => (
    filter.VALUES_MAP[value] && value !== 'any'
    ? builder.add('where', { ...filter.VALUES_MAP[value] })
    : null
  ),
  getValueForField: value => (
    value && value.length && value !== 'any'
    ? value
    : 'any'
  ),
  getValueForQuery: value => (value === 'any' ? undefined : value),
  field: ['FieldSelect', {
    name: 'storageType',
    placeholder: 'Storage Type',
    defaultValue: 'any',
    initialValue: 'any',
    prepareValueForInput: value => (value ? value : 'any'),
    choices: [{
      label: 'Any Storage',
      value: 'any',
    }, {
      label: Types.SHIPPING_STORAGE_LABELS_MAP.LOCAL || 'Local Warehouse',
      value: 'local',
    }, {
      label: Types.SHIPPING_STORAGE_LABELS_MAP.WAREHOUSE || 'Packman Warehouse',
      value: 'warehouse',
    }],
  }],
}];

// export const [
//   filterStockLocal,
//   filterStockWarehouse,
// ] = Types.SHIPPING_STORAGE.map(storageType => (
//   (values => ['FilterField', {
//     id: `stock-${storageType.toLowerCase()}`,
//     label: null,
//     section: 'top',
//     VALUES_MAP: values.reduce(
//       (agr, [key, , value]) => {
//         agr[key] = value || null;
//         return agr;
//       },
//       {},
//     ),
//     buildQuery: (value, builder, filter) => (
//         filter.VALUES_MAP[value]
//         && builder.add('where', { ...filter.VALUES_MAP[value] })
//     ),
//     getValueForField: getValueForFieldDefault,
//     field: ['FieldSelect', {
//       id: `stock-${storageType.toLowerCase()}`,
//       label: null,
//       placeholder: storageType,
//       defaultValue: 'any',
//       initialValue: 'any',
//       prepareValueForInput: value => (value ? value : 'any'),
//       choices: values.map(([key, label]) => ({ label, value: key })),
//     }],
//     skip: [
//       (props) => (
//         (
//             props.viewer?.role === 'ADMIN'
//           ? Types.SHIPPING_STORAGE
//           : (
//                 props.viewer?.storeType === 'MERCHANT'
//               ? (
//                     props.viewer?.storeData?.storageOptions?.length
//                   ? props.viewer.storeData.storageOptions
//                   : [null]
//                 )
//               : props.viewer?.storeType === 'RESELLER'
//               ? ['WAREHOUSE']
//               : [null]
//             )
//         ).includes(storageType) === false
//       ),
//     ],
//   }])([
//     [
//       'any',
//       `${Types.SHIPPING_STORAGE_LABELS_MAP[storageType]} - Any Stock`,
//     ],
//     [
//       'in',
//       `${Types.SHIPPING_STORAGE_LABELS_MAP[storageType]} - In Stock`,
//       { [`stockAggregates.${storageType}`]: { GTE: 1 } },
//     ],
//     [
//       'out',
//       `${Types.SHIPPING_STORAGE_LABELS_MAP[storageType]} - Out Of Stock`,
//       { [`stockAggregates.${storageType}`]: { EQ: 0 } },
//     ],
//   ])
// ));

export const [
  filterStockLocalAbove,
  filterStockLocalUnder,
  filterStockWarehouseAbove,
  filterStockWarehouseUnder,
] = Types.SHIPPING_STORAGE.reduce(
  (agr, storageType) => {
    [
      ['GTE', 'Min'],
      ['LTE', 'Max'],
    ].forEach(([operator, label]) => {
      agr.push(['FilterField', {
        id: `stock-${storageType.toLowerCase()}-${operator.toLowerCase()}`,
        label: null,
        section: 'top',
        buildQuery: (value, builder) => {
          if (value && value.length) {
            value = parseInt(value, 10);
            if (Number.isFinite(value)) {
              builder.add(
                'where',
                {
                  [`stockAggregates.${storageType}`]: {
                    [operator]: Math.max(value, operator === 'GTE' ? 0 : 0),
                  },
                },
              );
            }
          }
        },
        getValueForField: value => value || '',
        getValueForQuery: (value) => {
          value = !value
          ? undefined
          : value.target
          ? value.target.value
          : value;
          return !value || !value.length
          ? undefined
          : value;
        },
        field: ['FieldText', {
          name: `stock-${storageType.toLowerCase()}-${operator.toLowerCase()}`,
          label: null,
          align: 'right',
          placeholder: `${
            Types.SHIPPING_STORAGE_LABELS_SHORT_MAP[storageType]
          } Stock ${
            label
          }`,
        }],
        skip: [
          (props) => (
            (
                props.viewer?.role === 'ADMIN'
              ? Types.SHIPPING_STORAGE
              : (
                    props.viewer?.storeType === 'MERCHANT'
                  ? (
                        props.viewer?.storeData?.storageOptions?.length
                      ? props.viewer.storeData.storageOptions
                      : [null]
                    )
                  : props.viewer?.storeType === 'RESELLER'
                  ? ['WAREHOUSE']
                  : [null]
                )
            ).includes(storageType) === false
          ),
        ],
      }]);
    });
    return agr;
  },
  [],
);

export const filterSort = (values => ['FilterField', {
  id: 'sort',
  label: null,
  section: 'top',
  VALUES_MAP: values.reduce(
    (agr, [key, , value]) => {
      agr[key] = value;
      return agr;
    },
    {},
  ),
  buildQuery: (value, builder, filter) => (
    filter.VALUES_MAP[value]
    ? builder.add('sort', { ...filter.VALUES_MAP[value] })
    : builder.add('sort', { ...filter.VALUES_MAP['date-new'] })
  ),
  getValueForField: getValueForFieldDefault,
  field: ['FieldSelect', {
    name: 'sort',
    placeholder: 'Sort',
    defaultValue: 'date-new',
    initialValue: 'date-new',
    prepareValueForInput: value => (value ? value : 'date-new'),
    choices: values.map(([key, label]) => ({ label, value: key })),
  }],
}])([
  ['date-new', 'Latest', { createdAt: -1 }],
  ['published-new', 'Realistic', { featured: -1, publishedAt: -1 }],
  // ['date-old', 'Date (oldest)', { createdAt: 1 }],
  ['price-low', 'Price asc.', { 'variations.0.price': 1 }],
  ['price-high', 'Price desc.', { 'variations.0.price': -1 }],
]);

export const selectNoVariantsAndVariations = ['FilterHidden', {
  id: 'selectNoVariantsAndVariations',
  name: 'selectNoVariantsAndVariations',
  buildQuery: (value, builder) => {
    builder.add('select', {
      variants: false,
      variations: false,
    });
  },
}];

export const filterExport = (['FilterField', {
  id: 'exportProducts',
  name: 'exportProducts',
  label: null,
  section: 'top',
  buildQuery: () => { },
  // eslint-disable-next-line max-len
  propsFromPage: ({ client, queryBuilder, restBaseServer, searchParams }) => ({
    client,
    queryBuilder,
    restBaseServer,
    searchParams,
  }),
  skip: [tests.viewerIsAdminAndDoesNotHavePermission('reportsExport')],
  field: ['Action', {
    name: 'exportProducts',
    title: 'Products Report',
    icon: 'download',
    type: 'default',
    style: { width: '100%' },
    onClick: (props) => clickAnchor({
      url: `${props.restBaseServer}/products/export`,
      query: {
        access_token: props.client.getAccessToken(),
        query: props.queryBuilder.compile(),
        systemCountry: props.searchParams.sc,
      },
    }),
  }],
}]);

export const filterExportTemplate = (['FilterField', {
  id: 'exportProductsTemplate',
  name: 'exportProductsTemplate',
  label: null,
  section: 'top',
  buildQuery: () => { },
  // eslint-disable-next-line max-len
  propsFromPage: ({ client, queryBuilder, restBaseServer, searchParams }) => ({
    client,
    queryBuilder,
    restBaseServer,
    searchParams,
  }),
  skip: [tests.viewerIsAdminAndDoesNotHavePermission('reportsExport')],
  field: ['Action', {
    name: 'exportProductsTemplate',
    title: 'Product Export',
    icon: 'export',
    type: 'default',
    style: { width: '100%' },
    onClick: (props) => clickAnchor({
      url: `${props.restBaseServer}/products/export-template`,
      query: {
        access_token: props.client.getAccessToken(),
        query: props.queryBuilder.compile(),
        systemCountry: props.searchParams.sc,
      },
    }),
  }],
}]);

export const filterSwitchView = ['FilterField', {
  id: 'view',
  label: null,
  section: 'top',
  getValueForField: (value, props) => {
    if (value) return value;

    switch (props.location.pathname) {
      case '/products':
      case '/store/products':
        return 'grid';
      case '/products/table':
      case '/store/products/table':
        return 'table';
      default:
        return 'grid';
    }
  },
  propsFromPage: ({ viewer, history, location }) => ({
    viewer,
    history,
    location,
  }),
  isAction: true,
  handleChange: () => { },
  buildQuery: () => { },
  field: ['FieldRadio', {
    isAction: true,
    name: 'date',
    label: null,
    prepareValueForInput: (val, props) => {
      const { history, viewer } = props;
      const productsPath = (
        viewer.role === 'ADMIN'
          ? '/products'
          : viewer.role === 'SELLER'
          ? '/store/products'
          : ''
      );
      if (
        val === 'table'
        && history.location.pathname !== `${productsPath}/table`
      ) {
        history.replace(`${productsPath}/table`);
      } else if (
        val === 'grid'
        && history.location.pathname !== `${productsPath}`
      ) {
        history.replace(`${productsPath}`);
      }

      return val;
    },
    defaultValue: 'grid',
    initialValue: 'grid',
    choices: [{
      label: 'Grid',
      value: 'grid',
    }, {
      label: 'Table',
      value: 'table',
    }],
    Component: RadioComponent,
  }],
}];

export const ADMIN = [
  ...[
    [filterName, 8],
    [filterStore, 8],
    [filterSort, 8],
    [filterSwitchView, 24],
    [filterPublished, 24],

    [filterType, 12, true],
    [filterCategory, 12, true],
    [filterStorageType, 12, true],
    [filterProblems, 24, true],

    // [filterStockLocal, 24, true],
    // [filterStockWarehouse, 24, true],

    [filterStockLocalAbove, 12, true],
    [filterStockLocalUnder, 12, true],
    [filterStockWarehouseAbove, 12, true],
    [filterStockWarehouseUnder, 12, true],

    [filterExport, 24, true],
    [filterExportTemplate, 24, true],
  ].map(args => getFilterWithCol(...args)),

  // selectNoVariantsAndVariations,
  pagination,
  populateStore,
  populateCategory,
  filterCatalogue,
];

export const SELLER = [
  ...[
    [filterName, 12],
    [filterSort, 12],
    [filterSwitchView, 24],

    [filterPublished, 24],

    [filterCategory, 12, true],
    [filterCollection, 12, true],
    [filterStorageType, 12, true],

    [filterStockLocalAbove, 12, true],
    [filterStockLocalUnder, 12, true],
    [filterStockWarehouseAbove, 12, true],
    [filterStockWarehouseUnder, 12, true],

    [filterExport, 24, true],
    [filterExportTemplate, 24, true],
  ].map(args => getFilterWithCol(...args)),

  // selectNoVariantsAndVariations,
  pagination,
  populateCategory,
  scopeToSeller,
  filterCatalogue,
];
